import {
  BracketRound,
  Group,
  RoundPhase,
  Schedule,
  SeededTeam,
  TimeWindow,
} from "../types/Tournament";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Verteilt `size` Setzplätze auf Gruppen der Größe `groupSize`. Liefert eine
 * flache Liste; je `groupSize` aufeinanderfolgende Einträge bilden eine Gruppe.
 *
 * Schema: Es gibt G = size/groupSize Gruppen. Gruppe g bekommt den Topseed g+1
 * plus einen Block der (groupSize-1) schwächsten verbleibenden Seeds, von hinten
 * zugeteilt. So erhält die stärkste Gruppe die schwächsten Gegner.
 *
 * Beispiel size=64, groupSize=4 -> Gruppe 1 [1,62,63,64], Gruppe 2 [2,59,60,61],
 * …, Gruppe 16 [16,17,18,19].
 */
export function buildGroupSlots(size: number, groupSize: number): number[] {
  const numGroups = size / groupSize;
  const blockLen = groupSize - 1;
  const slots: number[] = [];
  for (let g = 0; g < numGroups; g++) {
    slots.push(g + 1); // Topseed der Gruppe
    const blockEnd = size - g * blockLen; // letzter (schwächster) Seed des Blocks
    for (let s = blockEnd - blockLen + 1; s <= blockEnd; s++) {
      slots.push(s);
    }
  }
  return slots;
}

/**
 * Anzahl Runden, bis aus `size` Teams in `groupSize`er-Gruppen (je 1 Sieger) ein
 * einzelnes Team übrig ist. Beispiel: 64 Teams, 4er-Gruppen -> 3 Runden
 * (64 -> 16 -> 4 -> 1).
 */
export function totalRounds(size: number, groupSize: number): number {
  return Math.round(Math.log(size) / Math.log(groupSize));
}

const roundCycleMs = (activeDays: number, pauseDays: number): number =>
  (activeDays + pauseDays) * DAY_MS;

/** Aktives Fenster einer Runde (für die Punktevergabe). */
export function getRoundWindow(
  roundIndex: number,
  tournamentStart: number,
  activeDays: number,
  pauseDays: number
): TimeWindow {
  const start = tournamentStart + roundIndex * roundCycleMs(activeDays, pauseDays);
  return { start, end: start + activeDays * DAY_MS };
}

/**
 * Lock-Zeitpunkt einer Runde = Ende des aktiven Fensters + `lockHours` Stunden
 * (Standard 12 = 12 Uhr mittags am Pausentag). Für gesperrte Runden zählt das
 * Backend nur Einträge mit loggedAt < lockTime, damit Nachträge das Ergebnis
 * nicht rückwirkend kippen. Liegt lockTime in der Zukunft, hat es keinen Effekt.
 */
export function getRoundLockTime(
  roundIndex: number,
  tournamentStart: number,
  activeDays: number,
  pauseDays: number,
  lockHours: number
): number {
  const window = getRoundWindow(roundIndex, tournamentStart, activeDays, pauseDays);
  return window.end + lockHours * 60 * 60 * 1000;
}

/**
 * Eine Runde ist gesperrt, sobald ihr Lock-Zeitpunkt erreicht ist
 * (12 Uhr mittags am Pausentag). Danach sind keine Nachträge mehr wirksam.
 */
export function isRoundLocked(
  roundIndex: number,
  now: number,
  tournamentStart: number,
  activeDays: number,
  pauseDays: number,
  lockHours: number
): boolean {
  return now >= getRoundLockTime(roundIndex, tournamentStart, activeDays, pauseDays, lockHours);
}

/**
 * Eine Runde ist entschieden, sobald ihr aktives Fenster vorbei ist
 * (also ab dem Pausentag der Runde).
 */
export function isRoundDecided(
  roundIndex: number,
  now: number,
  tournamentStart: number,
  activeDays: number,
  pauseDays: number
): boolean {
  const window = getRoundWindow(roundIndex, tournamentStart, activeDays, pauseDays);
  return now >= window.end;
}

export function getSchedule(
  now: number,
  tournamentStart: number,
  activeDays: number,
  pauseDays: number,
  size: number,
  groupSize: number
): Schedule {
  const rounds = totalRounds(size, groupSize);
  const cycle = roundCycleMs(activeDays, pauseDays);
  const activeMs = activeDays * DAY_MS;

  if (now < tournamentStart) {
    return { phase: "pre", currentRoundIndex: 0, dayInRound: 0, totalRounds: rounds };
  }

  const elapsed = now - tournamentStart;
  const roundIndex = Math.floor(elapsed / cycle);

  if (roundIndex >= rounds) {
    return {
      phase: "done",
      currentRoundIndex: rounds - 1,
      dayInRound: 0,
      totalRounds: rounds,
    };
  }

  const within = elapsed % cycle;
  let phase: RoundPhase;
  let dayInRound = 0;
  if (within < activeMs) {
    phase = "active";
    dayInRound = Math.floor(within / DAY_MS) + 1;
  } else {
    phase = "pause";
  }

  return { phase, currentRoundIndex: roundIndex, dayInRound, totalRounds: rounds };
}

/** Setzplatz (1..size) -> Team. Top `size` Teams aus der Setzungs-Rangliste. */
export function buildSeededTeams(
  ranking: { id: string; name: string; divisionName?: SeededTeam["divisionName"] }[],
  size: number
): Map<number, SeededTeam> {
  const map = new Map<number, SeededTeam>();
  for (let i = 0; i < size && i < ranking.length; i++) {
    map.set(i + 1, {
      id: ranking[i].id,
      name: ranking[i].name,
      seed: i + 1,
      divisionName: ranking[i].divisionName,
    });
  }
  return map;
}

/**
 * Bestimmt den Sieger einer Gruppe. Rückgabe ist der Slot-Index des Siegers oder
 * undefined, wenn (noch) kein Sieger feststeht.
 * - Genau ein Team vorhanden -> Freilos, steigt immer auf.
 * - forceHigherSeed (Debug) -> bestgesetztes Team (kleinste Seed-Nr.) gewinnt.
 * - Sonst nur bei entschiedener Runde: höchste Punktzahl gewinnt; bei Gleichstand
 *   das bessergesetzte Team (kleinere Seed-Nr.).
 */
function decideGroupWinner(
  teams: (SeededTeam | undefined)[],
  scores: number[],
  decided: boolean,
  forceHigherSeed: boolean
): number | undefined {
  const present = teams
    .map((team, index) => ({ team, index }))
    .filter((x): x is { team: SeededTeam; index: number } => !!x.team);

  if (present.length === 0) return undefined;
  if (present.length === 1) return present[0].index;

  if (forceHigherSeed) {
    return present.reduce((best, x) => (x.team.seed < best.team.seed ? x : best)).index;
  }
  if (!decided) return undefined;

  let best = present[0];
  for (const x of present.slice(1)) {
    const sx = scores[x.index];
    const sb = scores[best.index];
    if (sx > sb || (sx === sb && x.team.seed < best.team.seed)) {
      best = x;
    }
  }
  return best.index;
}

/**
 * Re-Seeding pro Runde: Gruppensieger werden nach ihren Punkten in dieser Runde
 * absteigend sortiert (Gleichstand -> bessere Ursprungssetzung) und per Snake-
 * Verfahren wieder auf Gruppen verteilt. Liefert die neue Teilnehmer-Reihenfolge
 * (flach, je groupSize = eine Gruppe) plus den anzuzeigenden Rang je Slot.
 *
 * Voraussetzung: Alle Einträge in `winners` sind definiert (vollständige Gruppen,
 * keine offenen Plätze beim Eingang ins Re-Seeding).
 */
function reseedParticipants(
  winners: (SeededTeam | undefined)[],
  prevScores: Map<string, number> | undefined,
  groupSize: number
): { participants: (SeededTeam | undefined)[]; ranks: number[] } {
  const teams = winners.filter((t): t is SeededTeam => !!t);
  const sorted = [...teams].sort((a, b) => {
    const sa = prevScores?.get(a.id) ?? 0;
    const sb = prevScores?.get(b.id) ?? 0;
    if (sb !== sa) return sb - sa; // mehr Punkte zuerst
    return a.seed - b.seed; // Gleichstand: bessere Ursprungssetzung zuerst
  });

  // Snake-Slots liefern pro Position den 1-indexierten Rang innerhalb dieser Runde.
  const slotRanks = buildGroupSlots(sorted.length, groupSize);
  const participants = slotRanks.map((rank) => sorted[rank - 1]);
  return { participants, ranks: slotRanks.slice() };
}

/**
 * Baut den Gruppen-Turnierbaum mit Re-Seeding pro Runde. `roundScores`:
 * roundIndex -> (teamId -> Punkte im Rundenfenster). `decidedRounds`: welche
 * Runden bereits entschieden sind (aktives Fenster vorbei).
 *
 * Runde 0 wird aus der Anfangssetzung (buildGroupSlots) gebildet; die angezeigte
 * Zahl ist die Ursprungssetzung. Ab Runde 1 ergeben sich die Gruppen aus den
 * Siegern + Punkten der jeweiligen Vorrunde (siehe reseedParticipants); die
 * angezeigte Zahl ist der neue Rundenrang. Ist eine Vorrunde noch nicht
 * entschieden, sind diese und alle folgenden Runden noch nicht bestimmbar und
 * werden als leere TBD-Gruppen ausgegeben.
 */
export function buildBracket(
  seededTeams: Map<number, SeededTeam>,
  size: number,
  groupSize: number,
  roundScores: Map<number, Map<string, number>>,
  decidedRounds: Set<number>,
  forceHigherSeed: boolean = false
): BracketRound[] {
  const numRounds = totalRounds(size, groupSize);
  const rounds: BracketRound[] = [];

  const slots = buildGroupSlots(size, groupSize);
  let participants: (SeededTeam | undefined)[] = slots.map((s) => seededTeams.get(s));
  let ranks: number[] = slots.slice(); // Runde 0: Anzeige = Ursprungssetzung
  let pairingKnown = true;

  for (let r = 0; r < numRounds; r++) {
    if (r >= 1) {
      if (pairingKnown && decidedRounds.has(r - 1)) {
        const reseeded = reseedParticipants(participants, roundScores.get(r - 1), groupSize);
        participants = reseeded.participants;
        ranks = reseeded.ranks;
      } else {
        // Gruppen noch nicht bestimmbar -> diese und folgende Runden TBD.
        const teamsInRound = participants.length;
        pairingKnown = false;
        participants = Array(teamsInRound).fill(undefined);
        ranks = Array(teamsInRound).fill(0);
      }
    }

    const scores = roundScores.get(r);
    const decided = decidedRounds.has(r);
    const groups: Group[] = [];
    const nextParticipants: (SeededTeam | undefined)[] = [];
    const numGroups = participants.length / groupSize;

    for (let g = 0; g < numGroups; g++) {
      const groupTeams: (SeededTeam | undefined)[] = [];
      const groupScores: number[] = [];
      const groupRanks: (number | undefined)[] = [];
      for (let j = 0; j < groupSize; j++) {
        const idx = g * groupSize + j;
        const team = participants[idx];
        groupTeams.push(team);
        groupScores.push(team ? (scores?.get(team.id) ?? 0) : 0);
        groupRanks.push(pairingKnown && team ? ranks[idx] : undefined);
      }
      const winner = decideGroupWinner(groupTeams, groupScores, decided, forceHigherSeed);

      groups.push({
        roundIndex: r,
        groupIndex: g,
        slots: groupTeams.map((team, j) => ({
          team,
          score: groupScores[j],
          rank: groupRanks[j],
        })),
        winner,
      });
      nextParticipants.push(winner != null ? groupTeams[winner] : undefined);
    }

    rounds.push({ roundIndex: r, groups });
    participants = nextParticipants;
  }

  return rounds;
}
